/**
 * InitialRequestUrl: The url that was used to load the very first page that
 * started the user's current browser session (or server-side-render).
 *
 * Only a limited number of fields are provided which are safe to access
 * across for the lifetime of the session (such as origin, host, hostname,
 * protocol, and port).
 *
 * Additionally, a number of configuration options, which are specified as
 * query string params on the first request, are also provided. Generally
 * these should be accessed through RequestInfo instead of here directly.
 * These params will also be passed through to every API requests that occurs
 * in this session, to make it easier to test things like country and lang.
 */
// We allow devs to set a schema mode by adding ?schemaMode=<mode>
// to the url when loading a page. This allows them to test how a
// page might look if a different service manages a field. Valid
// schemaModes are 'normal', 'preview' and 'manual'.
enum SchemaMode {
    Normal = "normal",
    Preview = "preview",
    Manual = "manual",
}

export const getFirstSubdomainParts = (hostname: string): Array<string> => {
    const match = hostname.match(/^(.*)\.khanacademy\.(org|dev|local|test)$/);
    if (!match) {
        return [];
    }

    const subdomainParts = match[1]
        .split(".")
        .filter((part) => part !== "zero");
    const firstSubdomain = subdomainParts[0];
    if (!firstSubdomain) {
        return [];
    }

    return firstSubdomain.split("--");
};

// We cache the client location as we want to only ever return the initial
// location that was used to render the page.
// To override this in Storybook, you can set the window.INITIAL_URL value.
let _cachedClientLocation;

const getInitialLocation = (): Readonly<URL> => {
    if (process.env.STORYBOOK) {
        /**
         * If we are in Storybook, this code could have loaded and run before
         * we had a chance to influence it. This can make it difficult to
         * override some of our Info object values for a specific story.
         * So, we provide a backdoor to change this value.
         */
        if (globalThis.INITIAL_URL) {
            return globalThis.INITIAL_URL;
        }
    }

    /**
     * We cache the result to ensure that on subsequent requests we always get
     * the first URL that was used to initially render the page.
     */
    if (!_cachedClientLocation) {
        // eslint-disable-next-line no-restricted-syntax -- We can't use `parseUrl` here as it would create a circular dependency with `url-utils.js`!
        _cachedClientLocation = new URL(window.location.href);
    }

    return _cachedClientLocation;
};

/**
 * Reset the initial location URL.
 *
 * This is used to reset the test environment between tests. It can also be
 * used to override the original location HREF.
 */
export const resetInitialLocation = (href?: string) => {
    if (process.env.NODE_ENV === "production") {
        // noop in production.
        return;
    }

    _cachedClientLocation = null;

    if (typeof window !== "undefined") {
        // eslint-disable-next-line no-restricted-syntax -- We explicitly want to set the browser location here.
        window.location.href = href ?? window.location.href;
    }
};

export default {
    get href(): string {
        return getInitialLocation().href;
    },

    get pathname(): string {
        return getInitialLocation().pathname;
    },

    // Some basic location details that are static and consistent for the
    // entirety of a user's visit.
    get origin(): string {
        return getInitialLocation().origin;
    },
    get host(): string {
        return getInitialLocation().host;
    },
    get hostname(): string {
        return getInitialLocation().hostname;
    },
    get protocol(): string {
        return getInitialLocation().protocol;
    },
    get port(): string {
        return getInitialLocation().port;
    },

    // These are search params that can be used to configure how the site
    // behaves. We want to also pass these params through to any GraphQL
    // requests that occur so that they use the same settings too. You
    // shouldn't be accessing these properties directly, instead please
    // use the values that are made available in RequestInfo as those will
    // also use the information that comes from Fastly.
    configParams: {
        get curriculum(): string {
            return getInitialLocation().searchParams.get("curriculum") || "";
        },
        get country(): string {
            return getInitialLocation().searchParams.get("country") || "";
        },
        get embedded(): string {
            return getInitialLocation().searchParams.get("embedded") || "";
        },
        get extension(): string {
            return getInitialLocation().searchParams.get("extension") || "";
        },
        get lang(): string {
            return getInitialLocation().searchParams.get("lang") || "";
        },
        get preview(): string {
            if (
                getFirstSubdomainParts(getInitialLocation().hostname).includes(
                    "preview",
                )
            ) {
                return "1";
            }
            return getInitialLocation().searchParams.get("preview") || "";
        },
        get publish_commit(): string {
            return (
                getInitialLocation().searchParams.get("publish_commit") || ""
            );
        },
        get region(): string {
            return getInitialLocation().searchParams.get("region") || "";
        },
        get schemaMode(): SchemaMode | null | undefined {
            // Return the schemaMode param value if a developer provided one.
            // This is passed along when making GraphQL requests.
            // For more context, see the GraphQL requst code.
            //
            // Note that this is somewhat brittle and is intended to be used
            // only for developers manually testing a field.
            const param: any =
                getInitialLocation().searchParams.get("schemaMode");
            if (param && Object.values(SchemaMode).includes(param)) {
                return param;
            }
            return null;
        },
    },
};
